import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Mail, Phone, MapPin, Calendar, User, Briefcase, MessageSquare, ExternalLink, Home, RefreshCw } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Admin = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/inquiries`);
      if (response.data.success) {
        setInquiries(response.data.inquiries);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load inquiries');
      setLoading(false);
      console.error(err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Premium Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-slate-950/98 via-emerald-950/95 to-slate-950/98 backdrop-blur-xl border-b border-emerald-500/20 shadow-[0_4px_30px_rgba(16,185,129,0.15)]">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-28">
            <div className="flex items-center gap-6">
              <Link to="/" className="flex items-center group -ml-4 mt-2">
                <div className="relative">
                  <div className="absolute -inset-3 bg-gradient-to-r from-emerald-500/40 via-green-400/30 to-emerald-500/40 rounded-2xl blur-xl opacity-70 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <img 
                    src="https://customer-assets-lqy194kg.emergentagent.net/job_90e246be-0711-4e27-8caa-13f1ea345ba5/artifacts/pl6ilas6_TNA%20Logo.png" 
                    alt="True North Alberta" 
                    className="relative h-24 w-auto rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.4),0_0_60px_rgba(16,185,129,0.3)] border-2 border-emerald-400/30"
                    style={{filter: 'drop-shadow(0 0 20px rgba(16,185,129,0.4))'}}
                  />
                </div>
              </Link>
              <div>
                <h1 className="text-2xl font-black text-white">Admin Panel</h1>
                <p className="text-sm text-emerald-400">Contact Inquiries Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="outline" className="gap-2 border-slate-600 text-gray-300 hover:text-white hover:border-emerald-500">
                  <Home className="w-4 h-4" />
                  Back to Website
                </Button>
              </Link>
              <Button onClick={fetchInquiries} className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats Section */}
      <section className="pt-36 pb-8 px-4 lg:px-8">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-400 text-sm uppercase tracking-wider">Total Inquiries</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-5xl font-black bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">{inquiries.length}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-400 text-sm uppercase tracking-wider">New</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-5xl font-black text-blue-400">
                  {inquiries.filter(i => i.status === 'new').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-400 text-sm uppercase tracking-wider">Contacted</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-5xl font-black text-amber-400">
                  {inquiries.filter(i => i.status === 'contacted').length}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 rounded-2xl">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-400 text-sm uppercase tracking-wider">Completed</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-5xl font-black text-green-400">
                  {inquiries.filter(i => i.status === 'completed').length}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Inquiries List */}
      <section className="py-8 px-4 lg:px-8">
        <div className="container mx-auto">
          <div className="mb-8">
            <h2 className="text-3xl font-black text-white">Contact <span className="bg-gradient-to-r from-emerald-400 to-green-400 bg-clip-text text-transparent">Inquiries</span></h2>
            <p className="text-gray-400 mt-2">View and manage all customer inquiries</p>
          </div>

          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-400">Loading inquiries...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-2xl p-4 mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {!loading && inquiries.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-400">No inquiries yet.</p>
            </div>
          )}

          <div className="space-y-6">
            {inquiries.map((inquiry) => (
              <Card key={inquiry.id} className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-2 border-slate-700/50 hover:border-emerald-500/30 rounded-3xl transition-all duration-500 hover:shadow-[0_20px_60px_rgba(16,185,129,0.1)]">
                <CardHeader className="border-b border-slate-700/50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-emerald-600 to-green-600 rounded-2xl flex items-center justify-center">
                        <User className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl text-white font-bold">{inquiry.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <CardDescription className="text-gray-400">{formatDate(inquiry.created_at)}</CardDescription>
                        </div>
                      </div>
                    </div>
                    <span className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${
                      inquiry.status === 'new' ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30' :
                      inquiry.status === 'contacted' ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
                      'bg-green-500/20 text-green-400 border border-green-500/30'
                    }`}>
                      {inquiry.status?.toUpperCase() || 'NEW'}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div className="space-y-4">
                      <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
                        <Mail className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Email</p>
                          <a href={`mailto:${inquiry.email}`} className="text-white hover:text-emerald-400 font-medium transition-colors">
                            {inquiry.email}
                          </a>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
                        <Phone className="w-5 h-5 text-emerald-400" />
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Phone</p>
                          <a href={`tel:${inquiry.phone}`} className="text-white hover:text-emerald-400 font-medium transition-colors">
                            {inquiry.phone}
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="space-y-4">
                      {inquiry.address && (
                        <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
                          <MapPin className="w-5 h-5 text-emerald-400" />
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Address</p>
                            <p className="text-white font-medium">{inquiry.address}</p>
                          </div>
                        </div>
                      )}
                      {inquiry.service && (
                        <div className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-xl">
                          <Briefcase className="w-5 h-5 text-emerald-400" />
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider">Service Interested In</p>
                            <p className="text-white font-medium">{inquiry.service}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {inquiry.message && (
                    <div className="bg-slate-800/50 rounded-2xl p-5 border border-slate-700/50">
                      <div className="flex items-start gap-4">
                        <MessageSquare className="w-5 h-5 text-emerald-400 mt-1" />
                        <div className="flex-1">
                          <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Message</p>
                          <p className="text-gray-300 leading-relaxed">{inquiry.message}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-4 mt-6">
                    <a href={`mailto:${inquiry.email}?subject=Re: Your inquiry about ${inquiry.service || 'our services'}`}>
                      <Button className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white font-bold rounded-xl">
                        <Mail className="mr-2 w-4 h-4" />
                        Send Email
                      </Button>
                    </a>
                    <a href={`tel:${inquiry.phone}`}>
                      <Button variant="outline" className="border-slate-600 text-gray-300 hover:text-white hover:border-emerald-500 rounded-xl">
                        <Phone className="mr-2 w-4 h-4" />
                        Call Now
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 lg:px-8 border-t border-slate-800">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-white mb-3">Quick Links</h3>
              <div className="flex gap-6">
                <Link to="/" className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2 transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  View Website
                </Link>
                <Link to="/services" className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2 transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  Services
                </Link>
                <Link to="/gallery" className="text-emerald-400 hover:text-emerald-300 flex items-center gap-2 transition-colors">
                  <ExternalLink className="w-4 h-4" />
                  Gallery
                </Link>
              </div>
            </div>
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} True North Alberta Property Services
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Admin;
